import React, { useEffect, useState } from 'react';
import { api, getListItems } from '../../services/api';
import { getImageUrl } from '../../utils/imageUrl';
import SocialLinks from '../Common/SocialLinks';

const PLATFORM_ICON = {
  youtube: 'fab fa-youtube',
  instagram: 'fab fa-instagram',
  tiktok: 'fab fa-tiktok',
  facebook: 'fab fa-facebook-f',
  other: 'fas fa-play',
};

function SocialWatchOne() {
  const [settings, setSettings] = useState({});
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settingsRes, videosRes] = await Promise.all([
          api.getSettings(),
          api.getSocialVideos({ per_page: 12 }),
        ]);
        if (cancelled) return;
        setSettings(settingsRes.data || {});
        setVideos(getListItems(videosRes));
      } catch {
        if (!cancelled) setVideos([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loading && videos.length === 0) {
    // Still show follow strip if profile links exist
    const hasProfiles = ['facebook_url', 'instagram_url', 'youtube_url', 'tiktok_url', 'whatsapp_url', 'twitter_url', 'linkedin_url']
      .some((k) => (settings[k] || '').trim());
    if (!hasProfiles) return null;
  }

  const subtitle = settings.social_section_subtitle || 'Follow Our Journey';
  const title = settings.social_section_title || 'Watch Reels, Videos & Posts';
  const intro = settings.social_section_intro || '';

  return (
    <section className="space twa-social-watch">
      <div className="container">
        <div className="title-area text-center">
          <span className="sub-title">{subtitle}</span>
          <h2 className="sec-title">{title}</h2>
          {intro && <p className="sec-text mx-auto" style={{ maxWidth: 640 }}>{intro}</p>}
          <div className="mt-3 d-flex justify-content-center">
            <SocialLinks />
          </div>
        </div>

        {loading && <p className="text-center">Loading videos…</p>}

        {!loading && videos.length > 0 && (
          <div className="row gy-4">
            {videos.map((item) => {
              const thumb = getImageUrl(item.thumbnail, 'social');
              const canEmbed = Boolean(item.embed_url);
              return (
                <div className="col-md-6 col-lg-4" key={item.id}>
                  <article className="twa-social-card">
                    <div className="twa-social-card__media">
                      {canEmbed && active === item.id ? (
                        <iframe
                          src={`${item.embed_url}?autoplay=1`}
                          title={item.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <button
                          type="button"
                          className="twa-social-card__play"
                          onClick={() => {
                            if (canEmbed) setActive(item.id);
                            else window.open(item.url, '_blank', 'noopener,noreferrer');
                          }}
                          aria-label={`Play ${item.title}`}
                        >
                          {thumb ? (
                            <img src={thumb} alt={item.title} />
                          ) : (
                            <div className="twa-social-card__placeholder">
                              <i className={PLATFORM_ICON[item.platform] || PLATFORM_ICON.other} />
                            </div>
                          )}
                          <span className="twa-social-card__badge">
                            <i className={PLATFORM_ICON[item.platform] || PLATFORM_ICON.other} />
                            {item.platform}
                          </span>
                          <span className="twa-social-card__icon"><i className="fas fa-play" /></span>
                        </button>
                      )}
                    </div>
                    <div className="twa-social-card__body">
                      <h3 className="box-title">{item.title}</h3>
                      {item.description && <p>{item.description}</p>}
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="line-btn"
                      >
                        Open on {item.platform}
                      </a>
                    </div>
                  </article>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export default SocialWatchOne;
